/**
 * guestUsagePrivacy.tsx — L4 (owner directive, 2026-07-15).
 *
 * In guest mode — viewer signed OUT — token-usage and cost details must not
 * be visible ANYWHERE in the chat UI, at all times. The ⓘ affordance stays,
 * but its popup content is replaced by a generic sign-in message with the
 * clickable /auth route. The gate is VIEW-TIME: a guest viewing any bubble,
 * including bubbles that carry usage data from storage (e.g. a thread from a
 * previous signed-in session on the same device), sees the generic message.
 *
 * CRITICAL INVARIANT (owner): signed-in behavior is completely unchanged —
 * every model usage during a signed-in inquiry renders exactly as before.
 *
 * Fail-closed: before Firebase auth resolves (or when it is unavailable in an
 * embedded host), the viewer is treated as signed out — usage details only
 * ever render for a CONFIRMED signed-in user.
 *
 * Copy note for the owner: the requested wording was "ensure to be signed in
 * to see usage costs"; it is normalized here to "Sign in to see usage costs."
 * (ES: "Inicia sesión para ver los costos de uso.") — flagged for review.
 */

import React, { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getUserUiLanguage } from '../../services/threadMetadata/uiLanguage';
import { AUTH_SIGN_IN_ROUTE } from './signInPromptCopy';

/** Full visible text of the guest usage notice (the leading verb is the link). */
export const GUEST_USAGE_NOTICE_TEXT_EN = 'Sign in to see usage costs.';
export const GUEST_USAGE_NOTICE_TEXT_ES = 'Inicia sesión para ver los costos de uso.';

/**
 * View-time auth signal for the usage-privacy gate. True whenever the viewer
 * is NOT a confirmed signed-in user (including pre-hydration and auth-
 * unavailable hosts — fail-closed). Subscribes to onAuthStateChanged so the
 * transition (guest signs in / member signs out) re-renders open popups.
 */
export function useViewerIsSignedOut(): boolean {
  const [signedOut, setSignedOut] = useState<boolean>(() => {
    try {
      return !getAuth().currentUser;
    } catch {
      return true; // fail-closed: unknown viewer never sees usage details
    }
  });
  useEffect(() => {
    try {
      return onAuthStateChanged(getAuth(), (user) => setSignedOut(!user));
    } catch {
      return undefined;
    }
  }, []);
  return signedOut;
}

/**
 * The generic popup body shown to signed-out viewers in place of ANY
 * usage/cost details (and in place of the "no extensive compute" fallback —
 * guests get ONE uniform message so the popup reveals nothing about compute).
 * Styling mirrors the popups' existing fallback block.
 */
export const GuestUsageNotice: React.FC = () => {
  const es = getUserUiLanguage() === 'es';
  return (
    <div
      data-testid="guest-usage-notice"
      style={{
        fontSize: '0.76rem',
        color: '#94a3b8',
        padding: '10px 12px',
        lineHeight: 1.45,
        background: 'rgba(0, 0, 0, 0.02)',
        borderRadius: '8px',
        border: '1px solid rgba(0, 0, 0, 0.03)',
      }}
    >
      <a
        href={AUTH_SIGN_IN_ROUTE}
        style={{ color: '#1e40af', fontWeight: 600, textDecoration: 'underline' }}
      >
        {es ? 'Inicia sesión' : 'Sign in'}
      </a>
      {es ? ' para ver los costos de uso.' : ' to see usage costs.'}
    </div>
  );
};

/**
 * The bot disclosure a signed-out visitor always sees (Cal. B&P §17941(b)).
 *
 * Twin of functions/src/compliance/botDisclosure.ts — CRA cannot import from
 * functions/, so the sentence lives in both and a test pins that they agree.
 * A disclosure the browser and the SMS lane word differently is two answers
 * to the same question.
 *
 * SELF-GATING on purpose: it calls useViewerIsSignedOut() itself and renders
 * nothing for a member, so the call sites are a bare one-liner and cannot get
 * the condition wrong. A member never sees it — T-R7 governs there, Prest0n
 * is labelled like a teammate with no AI badge, and 39% of consumers report
 * trusting a brand LESS for heavy visible AI labelling. Disclose where
 * somebody genuinely cannot tell; nowhere else.
 *
 * Persistent rather than per-message: the statute asks for a disclosure
 * "reasonably designed to inform", not a badge on every bubble.
 */
export const GUEST_BOT_DISCLOSURE_EN = 'Prest0n is an AI assistant, not a person.';
export const GUEST_BOT_DISCLOSURE_ES = 'Prest0n es un asistente de IA, no una persona.';

export const GuestBotDisclosureLine: React.FC = () => {
  const signedOut = useViewerIsSignedOut();
  if (!signedOut) return null;
  const spanish = getUserUiLanguage() === 'es';
  return (
    <div
      data-guest-bot-disclosure=""
      style={{
        fontSize: '0.7rem',
        // The AA-passing muted grey — this is real information, not decoration,
        // and it has to be readable to be a disclosure at all.
        color: 'var(--brand-text-muted)',
        textAlign: 'center',
        padding: '0.25rem 0.75rem 0.15rem',
        lineHeight: 1.4,
      }}
    >
      {spanish ? GUEST_BOT_DISCLOSURE_ES : GUEST_BOT_DISCLOSURE_EN}
    </div>
  );
};
